'use client';

import { c as _c } from "react/compiler-runtime";
import BazaarvoiceReviewToolbarComponent, { ALL_RATINGS_FILTER_VALUE } from './bazaarvoice-review-toolbar.component';
import { ProductReviewFilterInput, ProductReviewSortCode, ProductReviewsStatus } from '@spa/generated/graphql/graphql';
import { ReactElement, memo, useEffect, useState } from 'react';
import { BAZAARVOICE_REVIEW_SECTION_ID } from './bazaarvoiceReviewSectionAnchor';
import BazaarvoiceReviewEmptyComponent from './bazaarvoice-review-empty.component';
import BazaarvoiceReviewListComponent from './bazaarvoice-review-list.component';
import BazaarvoiceReviewLoadingComponent from './bazaarvoice-review-loading.component';
import BazaarvoiceReviewOverviewComponent from './bazaarvoice-review-overview.component';
import BazaarvoiceReviewPaginationComponent from './bazaarvoice-review-pagination.component';
import BazaarvoiceReviewPhotosGallery from './bazaarvoice-review-photos-gallery';
import BazaarvoiceReviewSuccessModalComponent from './bazaarvoice-review-success-modal.component';
import BazaarvoiceReviewUnavailableComponent from './bazaarvoice-review-unavailable.component';
import BazaarvoiceWriteReviewModalComponent from './bazaarvoice-write-review-modal.component';
import { GetProductReviewsResult } from '@spa/lib/graphql/product-reviews/getProductReviews';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { RATING_FILTER_CODE } from './bazaarvoiceReviewFilterCode';
import styles from './bazaarvoice-review-section.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useProductReviewsAPI from '@spa/lib/react/hooks/useProductReviewsAPI';
export interface Props {
  readonly productCode: string;
}
const PAGE_SIZE = 6;
const DEFAULT_SORT = ProductReviewSortCode.NEWEST;
type RequestState = 'loading' | 'success' | 'error';
const buildFilters = (ratingFilterValue: string): ReadonlyArray<ProductReviewFilterInput> | null => ratingFilterValue === ALL_RATINGS_FILTER_VALUE ? null : [{
  code: RATING_FILTER_CODE,
  values: [ratingFilterValue]
}];
function BazaarvoiceReviewSectionComponent(t0) {
  const $ = _c(58);
  const {
    productCode
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("bazaarvoice-review-section");
  const {
    getProductReviews
  } = useProductReviewsAPI();
  const [previousProductCode, setPreviousProductCode] = useState(productCode);
  const [searchText, setSearchText] = useState("");
  const [ratingFilterValue, setRatingFilterValue] = useState(ALL_RATINGS_FILTER_VALUE);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [pageIdx, setPageIdx] = useState(0);
  const [requestState, setRequestState] = useState("loading");
  const [reviews, setReviews] = useState(null);
  const [isWriteReviewModalOpen, setIsWriteReviewModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  if (productCode !== previousProductCode) {
    setPreviousProductCode(productCode);
    setSearchText("");
    setRatingFilterValue(ALL_RATINGS_FILTER_VALUE);
    setSort(DEFAULT_SORT);
    setPageIdx(0);
    setReviews(null);
    setRequestState("loading");
    setIsWriteReviewModalOpen(false);
    setIsSuccessModalOpen(false);
  }
  let t1;
  let t2;
  if ($[0] !== getProductReviews || $[1] !== pageIdx || $[2] !== productCode || $[3] !== ratingFilterValue || $[4] !== searchText || $[5] !== sort) {
    t1 = () => {
      let isCancelled = false;
      setRequestState("loading");
      getProductReviews(productCode, {
        pageIdx,
        pageSize: PAGE_SIZE,
        sort,
        search: searchText || null,
        filters: buildFilters(ratingFilterValue)
      }).then(result => {
        if (!isCancelled) {
          setReviews(result);
          setRequestState("success");
        }
      }).catch(() => {
        if (!isCancelled) {
          setReviews(null);
          setRequestState("error");
        }
      });
      return () => {
        isCancelled = true;
      };
    };
    t2 = [productCode, pageIdx, sort, searchText, ratingFilterValue, getProductReviews];
    $[0] = getProductReviews;
    $[1] = pageIdx;
    $[2] = productCode;
    $[3] = ratingFilterValue;
    $[4] = searchText;
    $[5] = sort;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = nextSearchText => {
      setSearchText(nextSearchText);
      setPageIdx(0);
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleSearchSubmit = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = nextSort => {
      setSort(nextSort);
      setPageIdx(0);
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleSortChange = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = nextRatingFilterValue => {
      setRatingFilterValue(nextRatingFilterValue);
      setPageIdx(0);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleRatingFilterChange = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsWriteReviewModalOpen(true);
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleWriteReviewClick = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsWriteReviewModalOpen(false);
      setIsSuccessModalOpen(true);
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleReviewSubmitted = t7;
  const isUnavailable = requestState === "error" || reviews?.status === ProductReviewsStatus.UNAVAILABLE;
  const isEmpty = reviews?.status === ProductReviewsStatus.EMPTY;
  const isAvailable = reviews?.status === ProductReviewsStatus.AVAILABLE;
  const showLoadingSkeleton = requestState === "loading" && reviews === null;
  let statusMessage;
  if (isEmpty) {
    let t8;
    if ($[13] !== formatMessage) {
      t8 = formatMessage("emptyStateMessage");
      $[13] = formatMessage;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    statusMessage = t8;
  } else {
    if (isAvailable && reviews) {
      let t8;
      if ($[15] !== formatMessage || $[16] !== reviews.overview || $[17] !== reviews.reviews.length) {
        t8 = reviews.reviews.length === 0 ? formatMessage("noMatchingReviewsMessage") : formatMessage("totalReviewCountLink", {
          totalReviewCount: reviews.overview.totalReviewCount
        });
        $[15] = formatMessage;
        $[16] = reviews.overview;
        $[17] = reviews.reviews.length;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      statusMessage = t8;
    } else {
      statusMessage = "";
    }
  }
  let t8;
  if ($[19] !== formatMessage) {
    t8 = formatMessage("sectionHeadline");
    $[19] = formatMessage;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <h2 className={styles["bazaarvoice-review-section__headline"]}>{t8}</h2>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== statusMessage) {
    t10 = <div className="sr-only" aria-live="polite" aria-atomic="true">{statusMessage}</div>;
    $[23] = statusMessage;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== showLoadingSkeleton) {
    t11 = showLoadingSkeleton && <BazaarvoiceReviewLoadingComponent reviewCount={PAGE_SIZE} />;
    $[25] = showLoadingSkeleton;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== isUnavailable) {
    t12 = isUnavailable && <BazaarvoiceReviewUnavailableComponent />;
    $[27] = isUnavailable;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== isEmpty) {
    t13 = isEmpty && <BazaarvoiceReviewEmptyComponent onWriteReviewClick={handleWriteReviewClick} />;
    $[29] = isEmpty;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== formatMessage || $[32] !== isAvailable || $[33] !== productCode || $[34] !== ratingFilterValue || $[35] !== requestState || $[36] !== reviews || $[37] !== searchText || $[38] !== sort) {
    t14 = isAvailable && reviews && <><BazaarvoiceReviewOverviewComponent overview={reviews.overview} onWriteReviewClick={handleWriteReviewClick} />{reviews.photoStrip.length > 0 && <BazaarvoiceReviewPhotosGallery id={`${BAZAARVOICE_REVIEW_SECTION_ID}-photos`} photos={reviews.photoStrip} />}<BazaarvoiceReviewToolbarComponent key={productCode} id={`${BAZAARVOICE_REVIEW_SECTION_ID}-toolbar`} searchText={searchText} sort={sort} ratingFilterValue={ratingFilterValue} ratingDistribution={reviews.overview.ratingDistribution} onSearchSubmit={handleSearchSubmit} onSortChange={handleSortChange} onRatingFilterChange={handleRatingFilterChange} disabled={requestState === "loading"} />{reviews.reviews.length === 0 ? <p className={styles["bazaarvoice-review-section__no-matching-reviews"]}>{formatMessage("noMatchingReviewsMessage")}</p> : <><BazaarvoiceReviewListComponent reviews={reviews.reviews} />{reviews.pagination.totalResults > PAGE_SIZE && <BazaarvoiceReviewPaginationComponent pagination={reviews.pagination} onPageChange={setPageIdx} disabled={requestState === "loading"} />}</>}</>;
    $[31] = formatMessage;
    $[32] = isAvailable;
    $[33] = productCode;
    $[34] = ratingFilterValue;
    $[35] = requestState;
    $[36] = reviews;
    $[37] = searchText;
    $[38] = sort;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t14) {
    t15 = <div className={styles["bazaarvoice-review-section__reviews"]}>{t11}{t12}{t13}{t14}</div>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsWriteReviewModalOpen(false);
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== isWriteReviewModalOpen || $[47] !== productCode) {
    t17 = <BazaarvoiceWriteReviewModalComponent isOpen={isWriteReviewModalOpen} onClosed={t16} productCode={productCode} onSubmitted={handleReviewSubmitted} />;
    $[46] = isWriteReviewModalOpen;
    $[47] = productCode;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setIsSuccessModalOpen(false);
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== isSuccessModalOpen) {
    t19 = <BazaarvoiceReviewSuccessModalComponent isOpen={isSuccessModalOpen} onClosed={t18} />;
    $[50] = isSuccessModalOpen;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t10 || $[53] !== t15 || $[54] !== t17 || $[55] !== t19 || $[56] !== t9) {
    t20 = <div id={BAZAARVOICE_REVIEW_SECTION_ID} className={styles["bazaarvoice-review-section"]}>{t9}{t10}{t15}{t17}{t19}</div>;
    $[52] = t10;
    $[53] = t15;
    $[54] = t17;
    $[55] = t19;
    $[56] = t9;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  return t20;
}
export default memo(BazaarvoiceReviewSectionComponent);